import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { currencyFormatter } from '../../../utils/currencyFormat';
import CustomPagination from '../../../components/customPagination';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { usePdfActions } from '../../common/DocumentPdf';
import { formatDate } from '../../../utils/formatDate';
import { useGetInvoicesHandler } from '../dataHandlers/getInvoicesHandler';
import DocumentTableStatusChip from '../../common/DocumentTableStatusChip';
import DocumentActions from '../../common/DocumentActions';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { OverallStatus } from '../../../utils/derivePeppolStatus';

const InvoiceTable = ({ handlePaginationChange, searchQuery = {}, isPurchase = false }) => {
  const { downloadPdf } = usePdfActions();
  const subscription = useSelector((state) => state.subscription.subscription);
  const orgSettings = useSelector((state) => state.org.org.settings);
  const navigate = useNavigate();
  const { invoices, totalDocuments, isLoading } = useGetInvoicesHandler({ searchQuery, isPurchase });
  const isPhase2Enabled = subscription?.features?.enable_phase2;
  const isPaymentGatewayEnabled =
    subscription?.features?.enable_telr_payment_gateway ||
    subscription?.features?.enable_geidea_payment_gateway ||
    false;
  const { eInvoiceForm } = useTranslatedMessages();
  const { document_tables } = eInvoiceForm;
  const tInvoice = document_tables.invoice;
  const tCommon = document_tables.common;

  const columns = [
    ...(isPhase2Enabled
      ? [
          {
            headerName: tInvoice.status,
            field: 'active_status',
            flex: 0.8,
            renderCell: (params) => {
              if (!params.row.is_phase2_document) {
                return <Box justifyContent={'center'}>{tCommon.empty_status}</Box>;
              }
              return (
                <DocumentTableStatusChip status={params?.row?.meta_info?.peppol_status?.overall_status ?? 'PENDING'} />
              );
            },
          },
        ]
      : []),
    {
      headerName: tInvoice.invoice_no,
      field: 'document_number',
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="span"
          variant="span"
          onClick={() =>
            navigate(
              isPurchase
                ? `/purchases/documents/invoices/${params.row.id}`
                : `/sales/documents/invoices/${params.row.id}`,
            )
          }
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: 'none',
            },
          }}
        >
          {params.value}
        </Typography>
      ),
    },

    {
      headerName: tInvoice.source,
      field: 'document_source',
      flex: 1,
      valueGetter: (params) => {
        return isPurchase ? (params.toLowerCase() !== 'peppol' ? tInvoice.source_self_billed : params) : params;
      },
    },

    {
      headerName: tInvoice.customer_info,
      field: 'accounting_customer_party',
      flex: 1,
      valueGetter: (params) => {
        if (!params) return tCommon.not_available;
        return params.name;
      },
    },
    {
      headerName: tInvoice.invoice_date,
      field: 'issue_date',
      flex: 1,
      valueGetter: (params) => {
        return formatDate(params);
      },
    },
    {
      headerName: tInvoice.amount,
      field: 'payable_amount',
      flex: 1,
      renderCell: ({ row }) =>
        row.document_currency_code === 'AED'
          ? `AED ${currencyFormatter().format(row.payable_amount)}`
          : currencyFormatter(row.document_currency_code).format(row.payable_amount),
    },
    {
      field: 'actions',
      headerName: tInvoice.actions,
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        let allowPDFDownload = true;
        let allowResubmit = false;
        let allowSendCustomPaymentLink = false;
        if (params.row.is_phase2_document) {
          const overallStatus = params?.row?.meta_info?.peppol_status?.overall_status;
          allowPDFDownload = overallStatus === OverallStatus.APPROVED;
          allowResubmit =
            (overallStatus === OverallStatus.REJECTED || overallStatus === OverallStatus.VALIDATION_FAILED) &&
            params.row.document_status !== 'OBSOLETE';
          allowSendCustomPaymentLink =
            isPaymentGatewayEnabled && overallStatus === OverallStatus.APPROVED && !isPurchase;
        }

        return (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: 0.6,
            }}
          >
            <>
              <Tooltip title={tCommon.download_document}>
                <IconButton
                  size="small"
                  onClick={() =>
                    downloadPdf({
                      url: `${isPurchase ? 'purchase' : 'sales'}-invoices/${params.row.id}/download-pdf`,
                      fileName: `INV-${params.row.id}.pdf`,
                    })
                  }
                  data-testid="download-pdf-button"
                  disabled={!allowPDFDownload}
                  sx={{
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  }}
                >
                  <PictureAsPdfIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </>
            <DocumentActions
              documentId={params?.row?.id}
              selectedInvoice={params?.row}
              documentType={'invoice'}
              documentApiBaseKey={'invoices'}
              isPurchase={isPurchase}
              allowIssueCreditNote={allowPDFDownload}
              allowResubmit={allowResubmit}
              allowSendCustomPaymentLink={allowSendCustomPaymentLink}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={invoices}
        columns={columns}
        getRowId={(row) => row.id}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default InvoiceTable;
